<template>
  <div>
    <div class="wz-page-header">
      <h1 class="wz-page-title">Sedes</h1>
    </div>

    <wz-table-container>

      <wz-table-toolbar>
        <template #left>
          <wz-filter-menu
            :options="filterOptions"
            :selected-option-filter="filters"
            @setFilters="setFilters"
          />
        </template>

        <template #right>
          <wz-tooltip text="Recargar">
            <wz-button variant="outline" @click="loadData()">
              <i class="ri-loop-right-line"></i>
            </wz-button>
          </wz-tooltip>
          <wz-search-bar
            placeholder="Buscar por nombre"
            @input="search = $event"
            @search="search = $event"
          />
          <wz-button @click="$router.push({ name: 'work-site-create' })">Agregar</wz-button>
        </template>

        <template #filters v-if="showFiltersList">
          <wz-badge
            v-for="(val, name) in filterList"
            :key="name"
            variant="primary"
            pill
            dismissible
            @dismiss="deleteFilter(name)"
          >
            {{ getFilterName(name, val) }}
          </wz-badge>
        </template>
      </wz-table-toolbar>

      <wz-table>
        <thead>
          <tr>
            <th class="wz-th-sortable" :class="{ 'is-sorted': sortKey === 'name' }" @click="toggleSort('name')">
              Nombre <i :class="['wz-th-sort-icon', sortIcon('name')]"></i>
            </th>
            <th class="wz-th-sortable" :class="{ 'is-sorted': sortKey === 'code' }" @click="toggleSort('code')">
              Código <i :class="['wz-th-sort-icon', sortIcon('code')]"></i>
            </th>
            <th class="wz-th-sortable" :class="{ 'is-sorted': sortKey === 'address' }" @click="toggleSort('address')">
              Dirección <i :class="['wz-th-sort-icon', sortIcon('address')]"></i>
            </th>
            <th>Empresa</th>
            <th class="wz-th-sortable" :class="{ 'is-sorted': sortKey === 'status' }" @click="toggleSort('status')">
              Estado <i :class="['wz-th-sort-icon', sortIcon('status')]"></i>
            </th>
            <th>Opciones</th>
          </tr>
        </thead>
        <tbody>
          <tr v-if="filteredList.length === 0">
            <td colspan="6" class="text-center text-muted py-4">No se encontraron resultados.</td>
          </tr>
          <tr v-for="o in sortedList(filteredList)" :key="o.id">
            <td>{{ o.name }}</td>
            <td>{{ o.code || '-' }}</td>
            <td>{{ o.address || '-' }}</td>
            <td>{{ (o.company && o.company.name) || '-' }}</td>
            <td>
              <wz-badge :variant="statusVariant(o.status)" pill>
                {{ getStatus(o.status).label }}
              </wz-badge>
            </td>
            <td style="text-align: center;">
              <wz-dropdown
                :items="[
                  { key: 'edit',   label: 'Editar' },
                  { key: 'status', label: getStatus(o.status).action },
                ]"
                placement="left"
                @click="handleAction(o, $event)"
              />
            </td>
          </tr>
        </tbody>
      </wz-table>

      <wz-table-footer>
        <span>{{ filteredList.length }} registro(s)</span>
      </wz-table-footer>

    </wz-table-container>

    <wz-confirm-modal
      v-model="confirmModal.show"
      :title="confirmModal.title"
      :description="confirmModal.description"
      :accept-text="confirmModal.acceptText"
      :cancel-text="confirmModal.cancelText"
      @confirm="onModalConfirm"
      @cancel="closeConfirmModal"
    />
  </div>
</template>

<script>
import { Alert } from "@wizepe/wizeui"
import { Util } from "@wizepe/wizeui"
import { FilterMixin } from "@wizepe/wizeui"
import { SortMixin, ConfirmModalMixin } from '@wizepe/wizeui'
import { Constants as LocalConstants } from '@/core/utils'
import { WorkSiteService, CompanyService } from '@/core/services'

export default {
  mixins: [SortMixin, ConfirmModalMixin, FilterMixin],
  data() {
    return {
      list:   [],
      search: '',
      filterOptions: [
        {
          label: 'Empresa',
          key: 'companyId',
          type: 'SINGLE',
          options: [],
        },
        {
          label: 'Estado',
          key: 'status',
          type: 'SINGLE',
          options: [
            LocalConstants.WorkSiteStatus.ACTIVE,
            LocalConstants.WorkSiteStatus.INACTIVE,
          ],
        },
      ],
    }
  },
  computed: {
    filteredList() {
      let result = this.list
      if (this.search) {
        const norm = (s) => (s || '').toLowerCase().normalize('NFD').replace(/[̀-ͯ]/g, '')
        const q = norm(this.search)
        result = result.filter(o => norm(o.name).includes(q) || norm(o.code).includes(q))
      }
      return result
    },
  },
  methods: {
    statusVariant(status) {
      return this.getStatus(status)?.color || 'dark'
    },
    handleAction(o, item) {
      if (item.key === 'edit')   this.$router.push({ name: 'work-site-edit', params: { id: o.id } })
      if (item.key === 'status') this.openChangeStatus(o)
    },
    async loadData() {
      try {
        await this.$store.dispatch('app/loading', true)
        const params = {}
        if (this.filters.companyId) params.companyId = this.filters.companyId
        if (this.filters.status != null) params.status = this.filters.status
        const resp = await WorkSiteService.list(params)
        this.list = resp.payload || []
      } catch (e) {
        console.error(e)
        Alert.error(e.errors ? e.errors.message : 'Error al cargar las sedes')
      } finally {
        await this.$store.dispatch('app/loading', false)
      }
    },
    getStatus(key) {
      return Util.searchValue(parseInt(key), LocalConstants.WorkSiteStatus)
    },
    openChangeStatus(o) {
      this.selectedOption = o
      this.openConfirmModal({
        title:       'Cambiar estado',
        description: '¿Está seguro que desea cambiar el estado de esta sede?',
        acceptText:  'Aceptar',
        action:      'update',
      })
    },
    onModalConfirm() {
      this.closeConfirmModal()
      if (this.confirmModal.action === 'update') this.changeStatus()
    },
    async changeStatus() {
      try {
        await this.$store.dispatch('app/loading', true)
        const current = parseInt(this.selectedOption.status)
        const newStatus = current === LocalConstants.WorkSiteStatus.ACTIVE.key
          ? LocalConstants.WorkSiteStatus.INACTIVE.key
          : LocalConstants.WorkSiteStatus.ACTIVE.key
        await WorkSiteService.changeStatus(this.selectedOption.id, newStatus)
        await this.loadData()
        Alert.success('Estado actualizado correctamente')
      } catch (ex) {
        console.error(ex)
        Alert.error(ex.errors ? ex.errors.message : 'Error al cambiar el estado')
      } finally {
        await this.$store.dispatch('app/loading', false)
      }
    },
  },
  async created() {
    try {
      const resp = await CompanyService.listAll()
      this.filterOptions[0].options = (resp.payload || []).map(c => ({ key: c.id, label: c.name }))
    } catch (e) {
      console.error(e)
    }
    await this.loadData()
  },
}
</script>

<style lang="stylus"></style>
